/**
 * 회사정보(institution) 관리 API — 통합설정 › 회사정보 (2026-08-25 신규).
 *
 * 관리 콘솔(8444)의 `admin/frontend/src/api/institution.ts` 를 그대로 옮겨 온 것이다.
 * 타입·함수 이름·반환 모양이 한 글자도 다르지 않다 — 두 화면이 같은 표를 고치므로,
 * 나중에 한쪽만 고쳐 어긋나는 일이 없도록 원본과 대조하기 쉬운 형태를 유지한다.
 *
 * ⚠ **경로만 다르다.** 콘솔이 부르는 `/admin/institution*` 은 **BFF 전용 이름**이고,
 *   BFF 는 그것을 chat-server `/api/v1/institution*` 으로 넘길 뿐이다
 *   (`admin/backend/src/routes/institution.ts`). 8443 의 apiClient 는 baseURL 이 이미
 *   `/api/v1` 이라 **원본 라우트를 직접** 부른다(서버·BFF 변경 0).
 *
 * 권한: 조회는 로그인만, 수정(PATCH)·로고 등록/삭제는 chat-server 가 ADMIN/SYSTEM_ADMIN 으로
 * 최종 판정한다. 사업자등록번호 체크섬·중복도 서버가 본다(화면 검증은 안내용).
 */
import { apiClient } from './client';

export interface Institution {
  id: string;
  code: string;
  name: string;
  domain: string | null;
  isActive: boolean;
  createdAt: string;
  // 회사정보 프로필 (install-bootstrap Part A) — 사업자등록번호는 표시용 ###-##-##### 포맷.
  businessRegNo: string | null;
  representativeName: string | null;
  businessType: string | null;
  businessItem: string | null;
  postalCode: string | null;
  address: string | null;
  addressDetail: string | null;
  phone: string | null;
  fax: string | null;
  companyEmail: string | null;
  /** 회사 로고 (2026-08-12) — `wclogo:{ts}` | http(s) URL | null(미등록). 사용자 사이트 브랜드에 반영. */
  logoUrl: string | null;
  /** 회사 직인 (2026-08-28 · 판 8) — 증명서에 찍힌다. 옛 서버는 안 준다 → undefined. */
  sealUrl?: string | null;
}

/** 현재 등록된 로고 미리보기 — base64(chat-server MinIO 객체) 또는 외부 URL. null = 미등록. */
export type InstitutionLogo =
  | { contentType: string; data: string; url?: undefined }
  | { url: string; contentType?: undefined; data?: undefined };

/** PATCH 가능 필드 — 전부 optional. name 외 null = 값 비움. */
export type InstitutionPatch = Partial<{
  name: string;
  domain: string | null;
  businessRegNo: string | null;
  representativeName: string | null;
  businessType: string | null;
  businessItem: string | null;
  postalCode: string | null;
  address: string | null;
  addressDetail: string | null;
  phone: string | null;
  fax: string | null;
  companyEmail: string | null;
}>;

export async function getInstitution(): Promise<Institution> {
  const { data } = await apiClient.get<{ institution: Institution }>('/institution');
  return data.institution;
}

export async function updateInstitution(patch: InstitutionPatch): Promise<Institution> {
  const { data } = await apiClient.patch<{ institution: Institution }>('/institution', patch);
  return data.institution;
}

// ── 회사 로고 ────────────────────────────────────────────────────────────────
// 업로드는 base64(JSON) — 서버 PUT 이 { contentType, data } 를 받는다(콘솔이 BFF 의 JSON 전용
// 프록시 때문에 그렇게 만든 규약이고, chat-server 라우트 자체가 그 모양이다). 서버 상한 1MB 는
// 업로드 전 리사이즈로 맞춘다(lib/image-resize.ts).
//
// ⚠ 조회는 **`?json=1` 을 직접 붙인다** — 콘솔에서는 BFF 가 대신 붙여 줬다. 안 붙이면 서버가
//   이미지 바이트(또는 302)를 그대로 흘려 보내 base64 미리보기가 깨진다.

/** 현재 로고 조회(미리보기). 미등록이면 null. */
export async function getInstitutionLogo(): Promise<InstitutionLogo | null> {
  const { data } = await apiClient.get<{ logo: InstitutionLogo | null }>('/institution/logo', { params: { json: '1' } });
  return data.logo ?? null;
}

/** 로고 등록/교체 — 갱신된 institution 반환(logoUrl 포함). */
export async function uploadInstitutionLogo(contentType: string, base64: string): Promise<Institution> {
  const { data } = await apiClient.put<{ institution: Institution }>(
    '/institution/logo',
    { contentType, data: base64 },
  );
  return data.institution;
}

/** 로고 삭제 — 사용자 화면은 기본 심볼로 복귀. */
export async function deleteInstitutionLogo(): Promise<Institution> {
  const { data } = await apiClient.delete<{ institution: Institution }>('/institution/logo');
  return data.institution;
}

/**
 * ── 회사 직인 (2026-08-28 · 자리 재정의 판 8) ───────────────────────────────
 *
 * 로고와 **같은 규약**이다(등록은 base64 PUT · 미리보기는 `?json=1`). 다른 것은 뜻뿐이다 —
 * 로고는 화면의 브랜드고 **직인은 문서에 찍히는 것**이다(증명서의 「직인」 자리).
 *
 * ★ 사람의 서명(`api/approval-signature.ts`)과도 주인이 다르다 — 그것은 본인이 만든다.
 */
export async function getInstitutionSeal(): Promise<InstitutionLogo | null> {
  const { data } = await apiClient.get<{ seal: InstitutionLogo | null }>('/institution/seal', { params: { json: '1' } });
  return data.seal ?? null;
}

export async function uploadInstitutionSeal(contentType: string, base64: string): Promise<Institution> {
  const { data } = await apiClient.put<{ institution: Institution }>(
    '/institution/seal',
    { contentType, data: base64 },
  );
  return data.institution;
}

/** 직인 삭제 — 증명서는 지금처럼 「직인」 글자만 찍는다(발급을 막지 않는다). */
export async function deleteInstitutionSeal(): Promise<Institution> {
  const { data } = await apiClient.delete<{ institution: Institution }>('/institution/seal');
  return data.institution;
}
